import { useDropzone } from 'react-dropzone';
import { ReactNode, SyntheticEvent, useRef, useState, useEffect, useCallback } from 'react';
import { ControllerRenderProps, FieldError } from 'react-hook-form';
import ReactCrop, { centerCrop, makeAspectCrop, type Crop } from 'react-image-crop';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogTitle, FormControl, FormLabel, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { grey } from '@mui/material/colors';
import UploadIcon from '@mui/icons-material/UploadFile';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { GradientIcon } from '../components';
import { fileSize } from '../../src/utils/fileSize';

import type { File as FileResource } from '../types/File';

export interface PictureUploaderFieldUIProps {
  id: string;
  name: string;
  label: string;
  disabled?: boolean;
  error?: FieldError;
  readOnly?: boolean;
  aspectRatio?: number;
  value: FileResource | null;
  onChange: ControllerRenderProps['onChange'];
}

const UPLOAD_MAX_SIZE = 10485760; // 10MB

export const PictureUploaderField = ({
  label = '',
  disabled = false,
  value,
  onChange,
  error,
  name,
  readOnly,
  aspectRatio,
  id,
}: PictureUploaderFieldUIProps) => {
  const theme = useTheme();

  const [showConfirm, setShowConfirm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [crop, setCrop] = useState<Crop | undefined>();
  const [showCrop, setShowCrop] = useState(false);
  const [fileName, setFileName] = useState('');
  const [imageSrc, setImageSrc] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Use a dedicated ref for cropping
  const cropImgRef = useRef<HTMLImageElement>(null);

  // Cleanup object URL for imageSrc when it changes or on unmount.
  useEffect(() => {
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  // Memoized simulated file removal API call.
  const handleConfirmFileRemove = useCallback(() => {
    setIsDeleting(true);

    setTimeout(() => {
      setIsDeleting(false);
      setShowConfirm(false);
      onChange(null);
    }, 500);
  }, [onChange]);

  const onImageLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;

      let previewCrop: Crop;
      if (aspectRatio) {
        previewCrop = centerCrop(makeAspectCrop({ unit: '%', width: 100 }, aspectRatio, width, height), width, height);
      } else {
        previewCrop = { x: 0, y: 0, width, height, unit: 'px' };
      }

      setCrop(previewCrop);
    },
    [aspectRatio],
  );

  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        setShowCrop(true);
        setFileName(acceptedFiles[0].name);

        // Revoke any previous URL before setting a new one.
        if (imageSrc) {
          URL.revokeObjectURL(imageSrc);
        }
        setImageSrc(URL.createObjectURL(acceptedFiles[0]));
      }
    },
    [imageSrc],
  );

  const handleUploadFile = useCallback(
    (file: File) => {
      setIsUploading(true);

      setTimeout(() => {
        setIsUploading(false);
        // Create a new preview URL for the cropped file.
        const newPreview = URL.createObjectURL(file);

        const newFileResource: Partial<FileResource> = {
          id,
          filename: file.name,
          size: file.size,
          preview: newPreview,
        };

        // update the form state with the new file resource
        onChange(newFileResource);
        // Update local imageSrc to point to the cropped file preview.
        setImageSrc(newPreview);
      }, 1000);
    },
    [onChange, id],
  );

  const handleConfirmCrop = useCallback(() => {
    const imageObj = cropImgRef.current;

    if (!imageObj || !crop) {
      console.error('Crop elements not available');
      return;
    }

    const canvas = document.createElement('canvas');
    const { x, y, width, height } = crop;
    const scaleX = imageObj.naturalWidth / imageObj.width;
    const scaleY = imageObj.naturalHeight / imageObj.height;

    canvas.width = width * scaleX;
    canvas.height = height * scaleY;

    const context = canvas.getContext('2d');

    if (context) {
      context.drawImage(imageObj, x * scaleX, y * scaleY, width * scaleX, height * scaleY, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], fileName, { type: 'image/png' });
          handleUploadFile(file);
        }
      }, 'image/png');
    }

    setShowCrop(false);
  }, [crop, fileName, handleUploadFile]);

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    multiple: false,
    accept: { 'image/jpeg': [], 'image/png': [] },
    disabled: disabled || readOnly,
    maxSize: UPLOAD_MAX_SIZE,
    onDrop: handleFileDrop,
  });

  const dropzoneIconColor = isDragReject ? theme.palette.error.main : theme.palette.primary.dark;
  const dropzoneBorderColor = isDragReject || error ? theme.palette.error.main : isDragAccept ? theme.palette.primary.main : grey[400];
  const dropzoneBgColor = isDragAccept ? theme.palette.success.light : isDragReject ? theme.palette.error.light : 'transparent';

  return (
    <FormControl fullWidth>
      {Boolean(label) && (
        <FormLabel
          sx={{
            mb: 2,
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: theme.typography.subtitle1.fontSize,
          }}
          id={`${name}-label`}
        >
          {label}
        </FormLabel>
      )}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Stack
            {...getRootProps()}
            minHeight="152px"
            alignItems="center"
            justifyContent="center"
            bgcolor={dropzoneBgColor}
            sx={{ cursor: 'pointer' }}
            borderRadius={theme.spacing(1)}
            border={`1px dashed ${dropzoneBorderColor}`}
          >
            <input {...getInputProps()} />

            <Stack alignItems="center" justifyContent="center" spacing={1}>
              {isUploading ? (
                <CircularProgress variant="indeterminate" />
              ) : (
                <>
                  <Box pt={1} pb={0.5}>
                    <UploadIcon sx={{ color: dropzoneIconColor }} />
                  </Box>

                  {!isDragActive && (
                    <Box>
                      <Typography display="inline" color="primary" sx={{ textDecorationLine: 'underline' }}>
                        Click to upload
                      </Typography>
                      <Typography display="inline"> or drag and drop</Typography>
                    </Box>
                  )}

                  {isDragAccept && (
                    <Typography color={theme.palette.primary.dark} fontWeight="bold">
                      Drop file(s) here to upload
                    </Typography>
                  )}

                  {isDragReject && <Typography color="error">File type, size or count is not allowed</Typography>}

                  <Box pt={0.5}>
                    <Typography display="inline" variant="subtitle2" color="secondary">
                      JPG, JPEG or PNG{' '}
                    </Typography>
                    <Typography display="inline" variant="subtitle2" color="secondary">
                      (max. 10MB)
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          </Stack>
          {error && (
            <Typography variant="caption" color="error">
              {error!.message as string}
            </Typography>
          )}
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          {value && value?.preview && (
            <>
              <Stack
                direction="row"
                sx={{ mb: 1, p: 2 }}
                alignItems="flex-start"
                justifyContent="space-between"
                borderRadius={theme.spacing(1)}
                border={`1px solid ${theme.palette.primary.main}`}
              >
                <Stack width="100%" direction="row" spacing={2} pb={1}>
                  <GradientIcon color="primary" IconComponent={ImageIcon} />

                  <Stack m={1} maxWidth="50%">
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {value.filename}
                    </Typography>

                    <Typography variant="subtitle2" color="secondary" fontSize="small">
                      Uploaded - {fileSize(value.size, 2)}
                    </Typography>
                  </Stack>
                </Stack>

                <CheckCircleRoundedIcon color="success" />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Tooltip title="View file">
                  <Button
                    color="primary"
                    variant="outlined"
                    sx={{ minWidth: 'auto' }}
                    aria-label={`View ${value.filename}`}
                    onClick={() => setShowPreview(true)}
                  >
                    <VisibilityOutlinedIcon />
                  </Button>
                </Tooltip>

                {!disabled && !readOnly && (
                  <Tooltip title="Remove file">
                    <Button
                      color="error"
                      variant="outlined"
                      sx={{ minWidth: 'auto' }}
                      aria-label={`Remove ${value.filename}`}
                      onClick={() => setShowConfirm(true)}
                    >
                      <DeleteOutlinedIcon />
                    </Button>
                  </Tooltip>
                )}
              </Stack>
            </>
          )}
        </Grid>
      </Grid>

      <UploadDialog isOpen={showConfirm} onClose={() => setShowConfirm(false)}>
        <DialogTitle variant="h4" component="h2">
          Are you sure you want to remove this file?
        </DialogTitle>
        <DialogActions>
          <Button disabled={isDeleting} variant="contained" color="error" onClick={handleConfirmFileRemove}>
            Remove
          </Button>
          <Button variant="outlined" color="warning" onClick={() => setShowConfirm(false)} disabled={isDeleting}>
            Keep
          </Button>
        </DialogActions>
      </UploadDialog>

      <UploadDialog isOpen={showPreview} onClose={() => setShowPreview(false)}>
        {/* Use the updated preview from value if available */}
        <img src={value?.preview || imageSrc} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </DialogActions>
      </UploadDialog>

      <UploadDialog onClose={() => setShowCrop(false)} isOpen={showCrop}>
        <DialogTitle variant="h4" component="h2">
          Crop picture
        </DialogTitle>
        <ReactCrop crop={crop} onChange={(c) => setCrop(c)}>
          <img ref={cropImgRef} src={imageSrc} alt="Crop" onLoad={onImageLoad} />
        </ReactCrop>
        <DialogActions>
          <Button disabled={!crop} loading={isDeleting} variant="contained" color="primary" onClick={handleConfirmCrop}>
            Confirm
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => setShowCrop(false)}>
            Cancel
          </Button>
        </DialogActions>
      </UploadDialog>
    </FormControl>
  );
};

interface UploadDialogProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

const UploadDialog = ({ children, isOpen, onClose }: UploadDialogProps) => {
  return (
    <Dialog open={isOpen} onClose={onClose} sx={{ zIndex: 1500 }} slotProps={{ paper: { sx: { p: 2 } } }}>
      {children}
    </Dialog>
  );
};
